import type { HubItem, SanityDocumentFile } from '@workspace/interfaces';

export const PAGE_SIZE = 9;

export const slugify = (s: string) => s.toLowerCase().trim().replace(/\s+/g, '-');

export const dedupeById = <T extends { id: string }>(arr: T[]) => {
  const map = new Map<string, T>();
  for (const it of arr) map.set(String(it.id), it);
  return Array.from(map.values());
};

// Returns true when a panel has subcategory autoFilters (used to mark sub-sections)
export const isSub = (panel: { autoFilter?: { subcategories?: unknown[] } | null }): boolean =>
  Array.isArray(panel?.autoFilter?.subcategories) && panel.autoFilter!.subcategories.length > 0;

// Maps raw Sanity document tag/name objects to { id, label } pairs for tag filters
export const mapPageTags = (
  tags: Array<{ name?: string } | null | undefined> | null | undefined,
): { id: string; label: string }[] =>
  (tags ?? [])
    .filter((t): t is { name: string } => Boolean(t?.name))
    .map((t) => ({ id: t.name, label: t.name }));

const getFileUrl = (file: SanityDocumentFile): string =>
  file.bynderDocument?.originalUrl ||
  file.bynderDocument?.selectedUrl ||
  file.bynderDocument?.url ||
  file.file?.url ||
  '';

const getThumbnailUrl = (file: SanityDocumentFile): string | null => {
  if (typeof file.thumbnailImage === 'string') return file.thumbnailImage;
  return file.thumbnailImage?.url ?? null;
};

const getUrlExtension = (url: string): string => {
  const seg = url.split('?')[0].split('/').pop() ?? '';
  const dot = seg.lastIndexOf('.');
  return dot >= 0 ? seg.slice(dot).toLowerCase() : '';
};

const getFilename = (file: SanityDocumentFile, fileUrl: string): string => {
  if (file.file?.originalFilename) return file.file.originalFilename;

  const urlExt = fileUrl ? getUrlExtension(fileUrl) : '';
  const urlSegment = fileUrl
    ? decodeURIComponent(fileUrl.split('?')[0].split('/').pop() || '')
    : '';

  if (file.bynderDocument?.name) {
    const name = file.bynderDocument.name;
    const hasExt = /\.[a-zA-Z0-9]+$/.test(name);
    return hasExt ? name : urlExt ? `${name}${urlExt}` : name;
  }

  return urlSegment || file.title || '';
};

export const mapFileToHubItem = (file: SanityDocumentFile): HubItem => {
  const fileUrl = getFileUrl(file);
  return {
    id: String(file._id ?? fileUrl),
    title: file.title ?? '',
    image: getThumbnailUrl(file),
    filename: getFilename(file, fileUrl),
    fileUrl,
    tags: (file.documentTags ?? []).map((tag) => tag.name).filter(Boolean) as string[],
  };
};
